import React, { useMemo, type FC } from "react";
import classNames from "classnames";
import { Button, ButtonIcon } from "@shared/ui/button";
import { Icon } from "@shared/ui/icon";
import { XmarkIcon } from "@omega/icons";
import "./CardHeader.scss";

/** Кнопка действий в шапке {@link CardHeader}. */
export interface CardHeaderButton {
    /** Спрятать и не отображать. */
    hide?: boolean;
    /** `icon` — icon-only; `button` — текстовая кнопка с опциональной иконкой. */
    type: "icon" | "button";
    /** Подпись / `aria-label` для icon-кнопки. */
    text: string;
    /** Путь к SVG-иконке. */
    srcIcon: string;
    /** Обработчик нажатия. */
    onClick: () => void;
}

/** Плотность заголовка: `compact` — overlay / ограниченная высота экрана. */
export type CardHeaderDensity = "default" | "compact";

/** Пропсы {@link CardHeader}. */
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Основной заголовок. */
    text: string;
    /** Вторичная строка под заголовком. */
    supportingText?: string;
    /** Кнопки действий справа / в content. */
    buttons?: Array<CardHeaderButton>;
    /** Показать кнопку закрытия слева. */
    onClose?: () => void;
    /**
     * Вертикальная плотность шапки.
     * @default "default"
     */
    density?: CardHeaderDensity;
}

/**
 * Универсальная шапка экрана / карточки / overlay: заголовок, supporting text, actions, close.
 */
export const CardHeader: FC<CardHeaderProps> = ({
    text,
    supportingText,
    buttons,
    onClose,
    density = "default",
    ...divProps
}) => {
    const iconButtons = useMemo<CardHeaderButton[] | null>(() => {
        if (buttons == null || buttons.length === 0) return null;
        return buttons.filter((x) => x.type === "icon" && x.hide !== true);
    }, [buttons]);

    const textButtons = useMemo<CardHeaderButton[] | null>(() => {
        if (buttons == null || buttons.length === 0) return null;
        return buttons.filter((x) => x.type === "button" && x.hide !== true);
    }, [buttons]);

    const hasSupportingText = supportingText != null && supportingText !== "";

    return (
        <div
            {...divProps}
            className={classNames("card_header", divProps.className, {
                "card_header--compact": density === "compact",
            })}
        >
            {onClose != null && (
                <ButtonIcon className="close_btn" size="sm" variant="ghost" aria-label="Закрыть" onClick={onClose}>
                    <XmarkIcon size={16} />
                </ButtonIcon>
            )}
            <div className="card_header_content">
                <div className="card_header_title_line">
                    <div className="header_title_line_text">{text}</div>
                    {hasSupportingText && <div className="header_title_line_supporting_text">{supportingText}</div>}
                </div>
                {textButtons != null && textButtons.length > 0 && (
                    <div className="card_header_buttons">
                        {textButtons.map((button, index) => (
                            <Button
                                key={index}
                                size="sm"
                                variant="primary"
                                startSlot={<Icon src={button.srcIcon} size="sm" />}
                                onClick={button.onClick}
                            >
                                {button.text}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
            {iconButtons != null && iconButtons.length > 0 && (
                <div className="icon_buttons">
                    {iconButtons.map((button, index) => (
                        <ButtonIcon key={index} size="sm" variant="ghost" aria-label={button.text} onClick={button.onClick}>
                            <Icon src={button.srcIcon} size="md" />
                        </ButtonIcon>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CardHeader;
